import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import {
  View,
  Flex,
  IconArrowOpenStartLine,
  IconArrowOpenEndLine,
  IconArrowOpenUpLine,
  IconArrowOpenDownLine,
  Popover,
} from '@smarterservices/smarter-ui';
import { FirstPaneContext, SecondPaneContext } from './SplitViewContext';
import './styles.css';

/**
 * SplitView Component
 *
 * A flexible view component that divides content into two resizable panes.
 * Supports both horizontal (left/right) and vertical (top/bottom) splits with
 * optional collapse/expand functionality.
 *
 * Features:
 * - Resizable panes with minimum size constraints
 * - Collapsible panes with customizable collapse size
 * - Controlled and uncontrolled modes for both splitting and collapsing
 * - Callbacks for resize and collapse events
 * - Keyboard accessibility with arrow key navigation
 * - Screen reader support with ARIA attributes
 * - Responsive design that fills its container
 * - Consistent visual feedback for focus and dragging states
 * - Direct DOM access and manipulation via ref
 *
 * Ref API (accessed via ref.current):
 * 
 * State Properties:
 * - isCollapsed: boolean - Current collapsed state
 * - splitPercentage: number - Current split percentage
 * - firstPaneDimensions: {width, height} - First pane dimensions
 * - secondPaneDimensions: {width, height} - Second pane dimensions
 * - isResized: boolean - Whether split has been resized from initial
 * - direction: string - Split direction ('horizontal' or 'vertical')
 * - collapsedPane: string - Which pane is collapsible
 * 
 * DOM Element Refs:
 * - firstPaneRef: HTMLElement - Direct reference to first pane DOM element
 * - secondPaneRef: HTMLElement - Direct reference to second pane DOM element
 * - dividerRef: HTMLElement - Direct reference to divider DOM element
 * 
 * Layout Control Methods:
 * - toggleCollapse() - Toggle collapse/expand state
 * - collapse() - Collapse the pane
 * - expand() - Expand the pane
 * - setSplitPercentage(percentage) - Set split percentage programmatically
 * - resetToDefault() - Reset split to default percentage
 * 
 * DOM Manipulation Methods (Remove from DOM):
 * - removeFirstPane() - Permanently remove first pane from DOM
 * - removeSecondPane() - Permanently remove second pane from DOM
 * - removeDivider() - Permanently remove divider from DOM
 * 
 * DOM Manipulation Methods (Show/Hide):
 * - hideFirstPane() - Hide first pane (display: none)
 * - showFirstPane() - Show first pane
 * - hideSecondPane() - Hide second pane and divider, expand first to 100%
 * - showSecondPane() - Show second pane and divider, restore layout
 * - hideDivider() - Hide just the divider
 * - showDivider() - Show the divider
 *
 * @example
 * const splitViewRef = useRef(null);
 * 
 * // Access DOM elements
 * const { firstPaneRef, secondPaneRef, dividerRef } = splitViewRef.current;
 * 
 * // Hide second pane and divider
 * splitViewRef.current.hideSecondPane();
 * splitViewRef.current.hideDivider();
 * 
 * // Remove from DOM permanently
 * splitViewRef.current.removeSecondPane();
 * 
 * @see README.md for detailed usage examples and API documentation
 */

/**
 * @typedef {Object} SplitViewProps
 * @property {React.ReactNode} firstContent - Content for the first pane (left or top)
 * @property {React.ReactNode} secondContent - Content for the second pane (right or bottom)
 * @property {'horizontal'|'vertical'} [direction='horizontal'] - Split direction ('horizontal' for left/right, 'vertical' for top/bottom)
 * @property {number} [initialSplit=50] - Initial split percentage (0-100)
 * @property {number} [defaultSplit=50] - Default split percentage for reset (double-click)
 * @property {number} [minFirstSize=20] - Minimum size of first pane in percentage
 * @property {number} [minSecondSize=20] - Minimum size of second pane in percentage
 * @property {string|number} [height='100%'] - Height of the container
 * @property {string|number} [width='100%'] - Width of the container
 * @property {string} [background='primary'] - Background color of the container
 * @property {function} [onResizeComplete] - Callback when resize is complete: (percentage) => void
 * @property {number} [splitPercentage] - Controlled split percentage
 * @property {function} [onChange] - Callback when split percentage changes: (percentage) => void
 * @property {number} [tabIndex=0] - Tab index for keyboard navigation
 * @property {boolean} [resizable=true] - Whether the layout can be resized
 * @property {boolean} [collapsible=false] - Whether panes can be collapsed
 * @property {number|string} [collapseSize=0] - Size to collapse to (percentage or specific units like '50px')
 * @property {'first'|'second'} [collapsedPane='first'] - Which pane to collapse
 * @property {boolean} [initialCollapsed=false] - Initial collapsed state (uncontrolled mode)
 * @property {boolean} [collapsed] - Controlled collapsed state
 * @property {function} [onCollapseChange] - Callback when collapse/expand state changes: (isCollapsed) => void
 * @property {React.ReactNode} [collapseTooltipContent] - Content for the collapse/expand tooltip
 * @property {string} [collapseTooltipPlacement] - Placement of the collapse/expand tooltip
 */
const SplitView = forwardRef(function SplitView(
  {
    firstContent,
    secondContent,
    direction = 'horizontal',
    initialSplit = 50,
    defaultSplit = 50,
    minFirstSize = 20,
    minSecondSize = 20,
    height = '100%',
    width = '100%',
    background = 'primary',
    onResizeComplete,
    splitPercentage: controlledSplitPercentage,
    onChange,
    // tabIndex allows controlling the tab order for multiple SplitLayout components
    tabIndex = 0,
    // Controls whether the layout can be resized by dragging
    resizable = true,
    // Controls whether to show collapse/expand buttons
    collapsible = false,
    // Size to collapse to when the collapse button is clicked (percentage or specific units like '50px', '10vh')
    collapseSize = 0,
    // Which pane to collapse (first or second)
    collapsedPane = 'first',
    // Initial collapsed state for uncontrolled mode
    initialCollapsed = false,
    // Controlled collapsed state
    collapsed: controlledCollapsed,
    // Callback when collapse/expand state changes
    onCollapseChange,
    collapseTooltipContent,
    collapseTooltipPlacement,
  },
  ref
) {
  // If either content is null/undefined, just render the non-null content without split
  const hasFirstContent = firstContent !== null && firstContent !== undefined;
  const hasSecondContent = secondContent !== null && secondContent !== undefined;

  // If both are null, render nothing
  if (!hasFirstContent && !hasSecondContent) {
    return null;
  }

  // If only one has content, render it directly without split
  if (!hasFirstContent) {
    return <View as="div" height={height} width={width} background={background}>{secondContent}</View>;
  }
  if (!hasSecondContent) {
    return <View as="div" height={height} width={width} background={background}>{firstContent}</View>;
  }

  // State to track if the divider is being dragged or focused
  const [isDragging, setIsDragging] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // State to track pane dimensions
  const [firstPaneDimensions, setFirstPaneDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [secondPaneDimensions, setSecondPaneDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [isResized, setIsResized] = useState(false);

  // Determine if component collapse state is controlled
  const isCollapseControlled = controlledCollapsed !== undefined;
  // Initialize with initialCollapsed for uncontrolled mode
  const [internalCollapsed, setInternalCollapsed] = useState(initialCollapsed);
  // Use controlled value if provided, otherwise use internal state
  const isCollapsed = isCollapseControlled
    ? controlledCollapsed
    : internalCollapsed;

  // Expose state and methods through ref
  useImperativeHandle(ref, () => {
    // Use either controlled or internal state
    const splitPercentage = isControlled
      ? controlledSplitPercentage
      : internalSplitPercentage;

    return {
      // Expose state
      isCollapsed,
      splitPercentage,
      firstPaneDimensions,
      secondPaneDimensions,
      isResized,
      direction,
      collapsedPane,

      // Expose DOM element refs
      firstPaneRef: firstPaneRef.current,
      secondPaneRef: secondPaneRef.current,
      dividerRef: dividerRef.current,

      // Expose methods
      toggleCollapse: () => toggleCollapse(),
      setSplitPercentage: (percentage) => updateSplitPercentage(percentage),
      collapse: () => {
        if (!isCollapsed) toggleCollapse();
      },
      expand: () => {
        if (isCollapsed) toggleCollapse();
      },
      resetToDefault: () => updateSplitPercentage(defaultSplit),

      // DOM manipulation methods
      removeFirstPane: () => {
        if (firstPaneRef.current && firstPaneRef.current.parentNode) {
          firstPaneRef.current.parentNode.removeChild(firstPaneRef.current);
        }
      },
      removeSecondPane: () => {
        if (secondPaneRef.current && secondPaneRef.current.parentNode) {
          secondPaneRef.current.parentNode.removeChild(secondPaneRef.current);
        }
      },
      removeDivider: () => {
        if (dividerRef.current && dividerRef.current.parentNode) {
          dividerRef.current.parentNode.removeChild(dividerRef.current);
        }
      },
      hideSecondPane: () => {
        // Batch DOM updates for better performance
        if (secondPaneRef.current) {
          secondPaneRef.current.style.cssText = 'display: none;';
        }
        if (dividerRef.current) {
          dividerRef.current.style.cssText = 'display: none;';
        }
        if (firstPaneRef.current) {
          firstPaneRef.current.style.cssText += 'width: 100% !important; height: 100% !important;';
        }
      },
      showSecondPane: () => {
        // Batch DOM updates for better performance
        if (secondPaneRef.current) {
          secondPaneRef.current.style.display = '';
        }
        if (dividerRef.current) {
          dividerRef.current.style.display = '';
        }
        if (firstPaneRef.current) {
          // Remove the inline styles to restore original layout
          firstPaneRef.current.style.width = '';
          firstPaneRef.current.style.height = '';
        }
      },
      hideDivider: () => {
        if (dividerRef.current) {
          dividerRef.current.style.display = 'none';
        }
      },
      showDivider: () => {
        if (dividerRef.current) {
          dividerRef.current.style.display = '';
        }
      },
      hideFirstPane: () => {
        if (firstPaneRef.current) {
          firstPaneRef.current.style.display = 'none';
        }
      },
      showFirstPane: () => {
        if (firstPaneRef.current) {
          firstPaneRef.current.style.display = '';
        }
      },
    };
  });

  // State to track previous split for expand operation
  const [previousSplit, setPreviousSplit] = useState(initialSplit);

  // References to pane elements for direct style manipulation
  const firstPaneRef = useRef(null);
  const secondPaneRef = useRef(null);
  const dividerRef = useRef(null);

  // Memoized ref callbacks to prevent unnecessary re-renders
  const setFirstPaneRef = useCallback((el) => {
    firstPaneRef.current = el;
  }, []);
  const setSecondPaneRef = useCallback((el) => {
    secondPaneRef.current = el;
  }, []);
  const setDividerRef = useCallback((el) => {
    dividerRef.current = el;
  }, []);
  const setContainerRef = useCallback((el) => {
    containerRef.current = el;
  }, []);

  // Track if the divider was dragged (to prevent collapse on click)
  const wasDragged = useRef(false);
  // Determine if component is controlled (splitPercentage prop provided) or uncontrolled
  const isControlled = controlledSplitPercentage !== undefined;
  const [internalSplitPercentage, setInternalSplitPercentage] =
    useState(initialSplit);

  // Validate proper usage of controlled mode
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      if (isControlled && typeof onChange !== 'function') {
        console.warn(
          'SplitView: You provided a `splitPercentage` prop without an `onChange` handler. ' +
          'This will render a non-resizable SplitView. ' +
          'If you want a resizable SplitView, either remove the `splitPercentage` prop to use uncontrolled mode, ' +
          'or provide an `onChange` handler to use controlled mode.'
        );
      }

      if (!isControlled && onChange) {
        console.warn(
          'SplitView: You provided an `onChange` handler without a `splitPercentage` prop. ' +
          'The component will use uncontrolled mode with `initialSplit`, and your `onChange` handler will still be called.'
        );
      }
    }
  }, [isControlled, onChange]);

  // Use either controlled or internal state
  const splitPercentage = isControlled
    ? controlledSplitPercentage
    : internalSplitPercentage;

  /**
   * Updates the split percentage, handling both controlled and uncontrolled modes
   *
   * In controlled mode (when splitPercentage prop is provided), it calls onChange with the new percentage
   * In uncontrolled mode, it updates the internal state directly
   * If in controlled mode without onChange handler, it falls back to uncontrolled behavior
   *
   * @param {number} newSplitPercentage - The new split percentage (0-100)
   */
  const updateSplitPercentage = (newSplitPercentage) => {
    // Clamp the percentage to ensure minimum sizes are respected
    const clampedPercentage = Math.min(
      Math.max(newSplitPercentage, minFirstSize),
      100 - minSecondSize
    );

    if (isControlled) {
      if (typeof onChange === 'function') {
        // Proper controlled mode with onChange handler
        onChange(clampedPercentage);
      } else {
        // Fallback to uncontrolled behavior when in controlled mode without onChange
        // This ensures the component is still resizable even when improperly configured
        setInternalSplitPercentage(clampedPercentage);
      }
    } else {
      // Standard uncontrolled mode
      setInternalSplitPercentage(clampedPercentage);

      // Still call onChange if provided (for hybrid usage)
      if (typeof onChange === 'function') {
        onChange(clampedPercentage);
      }
    }
  };

  /**
   * Calculate the pixel equivalent of a CSS size value
   * @param {string} sizeValue - CSS size value (e.g., '50px', '10vh')
   * @param {boolean} isWidth - Whether this is a width measurement (true) or height (false)
   * @returns {number} Size in pixels
   */
  const calculatePixelSize = (sizeValue, isWidth) => {
    // For simple px values, parse directly
    if (sizeValue.endsWith('px')) {
      return parseFloat(sizeValue);
    }

    // For other units, use DOM measurement
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style[isWidth ? 'width' : 'height'] = sizeValue;
    document.body.appendChild(tempDiv);

    // Get the computed size in pixels
    const computedSize = isWidth ? tempDiv.offsetWidth : tempDiv.offsetHeight;

    // Remove the temporary div
    document.body.removeChild(tempDiv);

    return computedSize;
  };

  /**
   * Toggles between collapsed and expanded states
   *
   * This function handles the collapse/expand functionality with the following behavior:
   * - When collapsing: stores the current split percentage to restore it later
   * - When expanding: restores the previous split percentage
   * - Supports both percentage values and absolute units (px, vh, vw, etc.)
   * - Handles both controlled and uncontrolled collapse states
   * - Calls onCollapseChange callback when provided
   *
   * The collapse size can be specified as a percentage (e.g., 20) or as a specific CSS unit (e.g., '50px', '10vh')
   */
  const toggleCollapse = () => {
    if (!isCollapsed) {
      // COLLAPSING: Store current split before collapsing
      setPreviousSplit(splitPercentage);

      // Handle different types of collapseSize
      if (typeof collapseSize === 'number') {
        // If collapseSize is a number, treat it as a percentage
        const collapseValue =
          collapsedPane === 'first' ? collapseSize : 100 - collapseSize;
        updateSplitPercentage(collapseValue);
      } else if (typeof collapseSize === 'string') {
        // For string values (e.g., '50px'), use direct DOM manipulation after render
        // Set a minimal percentage to keep the layout stable
        const minimalPercentage = 5;

        if (collapsedPane === 'first') {
          updateSplitPercentage(minimalPercentage);

          // Apply the exact size immediately using requestAnimationFrame for smooth transition
          requestAnimationFrame(() => {
            if (firstPaneRef.current && secondPaneRef.current) {
              // Batch style updates using cssText for better performance
              const dimension = isHorizontal ? 'width' : 'height';
              firstPaneRef.current.style.cssText += `${dimension}: ${collapseSize}; flex: 0 0 auto; overflow: hidden; max-height: ${isHorizontal ? '100%' : collapseSize};`;
              secondPaneRef.current.style.cssText += `flex: 1 1 auto; ${dimension}: auto; overflow: hidden;`;
            }
          });
        } else {
          updateSplitPercentage(100 - minimalPercentage);

          // Apply the exact size immediately using requestAnimationFrame for smooth transition
          requestAnimationFrame(() => {
            if (firstPaneRef.current && secondPaneRef.current) {
              // Batch style updates using cssText for better performance
              const dimension = isHorizontal ? 'width' : 'height';
              secondPaneRef.current.style.cssText += `${dimension}: ${collapseSize}; flex: 0 0 auto; overflow: hidden; max-height: ${isHorizontal ? '100%' : collapseSize};`;
              firstPaneRef.current.style.cssText += `flex: 1 1 auto; ${dimension}: auto; overflow: hidden;`;
            }
          });
        }
      }

      // Update collapsed state
      if (!isCollapseControlled) {
        setInternalCollapsed(true);
      }

      // Call the callback if provided
      if (typeof onCollapseChange === 'function') {
        onCollapseChange(true);
      }
    } else {
      // EXPANDING: Restore previous split when expanding
      updateSplitPercentage(previousSplit);

      // Reset any direct style manipulations immediately using requestAnimationFrame
      requestAnimationFrame(() => {
        if (firstPaneRef.current && secondPaneRef.current) {
          // Batch reset all styles at once for better performance
          const resetStyles = 'flex: ""; width: ""; height: ""; overflow: ""; max-height: ""; display: "";';
          firstPaneRef.current.style.cssText = firstPaneRef.current.style.cssText.replace(/flex:[^;]*;|width:[^;]*;|height:[^;]*;|overflow:[^;]*;|max-height:[^;]*;|display:[^;]*;/g, '');
          secondPaneRef.current.style.cssText = secondPaneRef.current.style.cssText.replace(/flex:[^;]*;|width:[^;]*;|height:[^;]*;|overflow:[^;]*;|max-height:[^;]*;|display:[^;]*;/g, '');
        }
      });

      // Update collapsed state
      if (!isCollapseControlled) {
        setInternalCollapsed(false);
      }

      // Call the callback if provided
      if (typeof onCollapseChange === 'function') {
        onCollapseChange(false);
      }
    }
  };

  const containerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startPosRef = useRef(0);
  const startSplitRef = useRef(0);
  const isHorizontal = direction === 'horizontal';
  const rafIdRef = useRef(null);

  /**
   * Handles mouse down on the divider
   *
   * This function initiates the dragging interaction with the following behavior:
   * - Initiates dragging only if the component is resizable and not collapsed
   * - Sets focus state to ensure consistent visual feedback with keyboard focus
   * - Prevents text selection during drag operations
   * - Tracks whether the divider was actually dragged or just clicked
   * - Sets up event listeners for mouse move and mouse up events
   *
   * The visual feedback includes the medium border radius and consistent focus
   * indicators across both mouse and keyboard interactions.
   *
   * @param {React.MouseEvent} e - The mouse event
   */
  const handleMouseDown = (e) => {
    // If not resizable and not collapsible, or if a pane is collapsed, don't allow dragging
    if ((!resizable && !collapsible) || isCollapsed) return;

    e.preventDefault(); // Prevent text selection during drag
    e.stopPropagation(); // Stop event bubbling

    // Only process left mouse button (button === 0)
    if (e.button !== 0) return;

    // Reset the dragged flag when starting a new interaction
    wasDragged.current = false;

    // Set both the ref and state for dragging
    isDraggingRef.current = true;
    setIsDragging(true); // Update state to trigger UI changes
    setIsFocused(true); // Also set focus state to ensure consistent styling

    startPosRef.current = isHorizontal ? e.clientX : e.clientY;
    startSplitRef.current = splitPercentage;
    document.body.style.cursor = isHorizontal ? 'col-resize' : 'row-resize';

    // Add event listeners to document to capture mouse movements outside the divider
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp);
  };

  /**
   * Handle mouse move during dragging
   * Calculates the new split percentage based on mouse position
   * Memoized to prevent recreation on every render
   *
   * @param {MouseEvent} e - The mouse event
   */
  const handleMouseMove = useCallback((e) => {
    // Prevent default to stop text selection and page scrolling
    e.preventDefault();

    if (!isDraggingRef.current || !containerRef.current) return;

    // Cancel any pending animation frame
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }

    // Set the dragged flag to true when there's actual movement
    // We use a threshold to avoid setting it for tiny unintentional movements
    const currentPos = isHorizontal ? e.clientX : e.clientY;
    const deltaPos = Math.abs(currentPos - startPosRef.current);
    if (deltaPos > 3) {
      // 3px threshold for considering it a drag vs a click
      wasDragged.current = true;
    }

    const containerSize = isHorizontal
      ? containerRef.current.offsetWidth
      : containerRef.current.offsetHeight;

    const deltaPercentage =
      ((currentPos - startPosRef.current) / containerSize) * 100;

    // Calculate new split percentage and enforce min sizes
    let newSplitPercentage = startSplitRef.current + deltaPercentage;
    newSplitPercentage = Math.round(
      Math.max(minFirstSize, Math.min(100 - minSecondSize, newSplitPercentage))
    );

    // Use requestAnimationFrame for smoother updates
    rafIdRef.current = requestAnimationFrame(() => {
      updateSplitPercentage(newSplitPercentage);
    });
  }, [isHorizontal, minFirstSize, minSecondSize, updateSplitPercentage]);

  /**
   * Handle mouse up to end dragging
   * Cleans up event listeners and calls onResizeComplete
   * Memoized to prevent recreation on every render
   */
  const handleMouseUp = useCallback(() => {
    if (!isDraggingRef.current) return;

    // Cancel any pending animation frame
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    const wasDragAction = wasDragged.current;

    // Clean up drag state
    isDraggingRef.current = false;
    setIsDragging(false);
    setIsFocused(false); // Reset focus state when dragging ends
    document.body.style.cursor = '';

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);

    // Only treat it as a resize completion
    if (typeof onResizeComplete === 'function') {
      onResizeComplete(splitPercentage);
    }
  }, [handleMouseMove, onResizeComplete, splitPercentage]);

  /**
   * Handle double click to reset to default split
   * Resets the split to the defaultSplit value
   */
  const handleDoubleClick = () => {
    updateSplitPercentage(defaultSplit);
    if (typeof onResizeComplete === 'function') {
      onResizeComplete(defaultSplit);
    }
  };

  /**
   * Handles keyboard navigation for accessibility
   *
   * Provides comprehensive keyboard support for both resizing and collapsing:
   * - Arrow keys: Adjust the split position when resizable
   *   - Left/Up: Decrease first pane size (move divider left/up)
   *   - Right/Down: Increase first pane size (move divider right/down)
   * - Enter/Space:
   *   - When not collapsed: Reset to default split (if double-tapped) or toggle collapse
   *   - When collapsed: Expand the collapsed pane
   *
   * The function respects the resizable and collapsible props and provides appropriate
   * behavior based on the current state. It also prevents default browser behavior
   * to ensure consistent interaction across browsers.
   *
   * @param {React.KeyboardEvent} e - The keyboard event
   */
  const handleKeyDown = (e) => {
    // If collapsed, only handle Enter/Space to expand
    if (isCollapsed) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleCollapse();
      }
      return;
    }

    // If not resizable and not collapsible, don't allow keyboard adjustments
    if (!resizable && !collapsible) return;

    let newPercentage = splitPercentage;
    const step = 5; // 5% step for keyboard navigation

    // Left/Up decreases first pane size, Right/Down increases it
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      newPercentage = Math.max(minFirstSize, splitPercentage - step);
      e.preventDefault();
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      newPercentage = Math.min(100 - minSecondSize, splitPercentage + step);
      e.preventDefault();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();

      // If collapsible, toggle collapse on Enter or Space
      if (collapsible) {
        toggleCollapse();
      } else {
        // Otherwise reset to default split
        newPercentage = defaultSplit;
        updateSplitPercentage(newPercentage);
        if (typeof onResizeComplete === 'function') {
          onResizeComplete(newPercentage);
        }
      }
      return; // Return early since we've handled the event
    } else {
      return; // Don't update for other keys
    }

    updateSplitPercentage(newPercentage);

    if (typeof onResizeComplete === 'function') {
      onResizeComplete(newPercentage);
    }
  };

  // Create a unique ID for this instance to ensure proper focus management
  const instanceId = useRef(
    `split-layout-${Math.random().toString(36).substr(2, 9)}`
  );

  // Store previous dimensions in refs to avoid dependencies that would cause re-renders
  const prevFirstDimensions = useRef({ width: 0, height: 0 });
  const prevSecondDimensions = useRef({ width: 0, height: 0 });

  // Track pane dimensions with ResizeObserver
  // This ONLY notifies children of their dimensions - it does NOT control pane sizing
  // Pane sizes are controlled purely by CSS percentages
  useEffect(() => {
    if (!firstPaneRef.current || !secondPaneRef.current) return;

    // Measure initial dimensions immediately
    const firstRect = firstPaneRef.current.getBoundingClientRect();
    const secondRect = secondPaneRef.current.getBoundingClientRect();

    prevFirstDimensions.current = { width: firstRect.width, height: firstRect.height };
    prevSecondDimensions.current = { width: secondRect.width, height: secondRect.height };

    setFirstPaneDimensions({ width: firstRect.width, height: firstRect.height });
    setSecondPaneDimensions({ width: secondRect.width, height: secondRect.height });

    // Large tolerance to prevent micro-adjustments from triggering updates
    const TOLERANCE = 5;
    let debounceTimer = null;

    const resizeObserver = new ResizeObserver((entries) => {
      // Clear existing timer
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      // Debounce to prevent rapid firing
      debounceTimer = setTimeout(() => {
        let shouldUpdateFirst = false;
        let shouldUpdateSecond = false;
        let newFirstDimensions = { ...prevFirstDimensions.current };
        let newSecondDimensions = { ...prevSecondDimensions.current };

        entries.forEach((entry) => {
          const { width, height } = entry.contentRect;

          if (entry.target === firstPaneRef.current) {
            const widthDiff = Math.abs(width - prevFirstDimensions.current.width);
            const heightDiff = Math.abs(height - prevFirstDimensions.current.height);

            // Only update if change is significant
            if (widthDiff > TOLERANCE || heightDiff > TOLERANCE) {
              newFirstDimensions = { width, height };
              shouldUpdateFirst = true;
            }
          } else if (entry.target === secondPaneRef.current) {
            const widthDiff = Math.abs(width - prevSecondDimensions.current.width);
            const heightDiff = Math.abs(height - prevSecondDimensions.current.height);

            // Only update if change is significant
            if (widthDiff > TOLERANCE || heightDiff > TOLERANCE) {
              newSecondDimensions = { width, height };
              shouldUpdateSecond = true;
            }
          }
        });

        // Update dimensions - this is READ-ONLY for children
        // Does NOT affect pane sizing (controlled by CSS percentages)
        if (shouldUpdateFirst) {
          prevFirstDimensions.current = newFirstDimensions;
          setFirstPaneDimensions(newFirstDimensions);
        }

        if (shouldUpdateSecond) {
          prevSecondDimensions.current = newSecondDimensions;
          setSecondPaneDimensions(newSecondDimensions);
        }
      }, 50); // 50ms debounce - reduced for faster response
    });

    // Observe both panes
    resizeObserver.observe(firstPaneRef.current);
    resizeObserver.observe(secondPaneRef.current);

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      resizeObserver.disconnect();
    };
  }, []); // Run once on mount

  // Update isResized state when split percentage changes
  useEffect(() => {
    // Only update if the value would actually change
    const newIsResized = splitPercentage !== initialSplit;
    if (isResized !== newIsResized) {
      setIsResized(newIsResized);
    }
  }, [splitPercentage, initialSplit, isResized]);

  // Apply initial collapsed state when component mounts (only for uncontrolled mode)
  useEffect(() => {
    if (!isCollapseControlled && initialCollapsed && firstPaneRef.current && secondPaneRef.current) {
      // Apply collapsed styles on mount
      const isHorizontal = direction === 'horizontal';

      requestAnimationFrame(() => {
        if (firstPaneRef.current && secondPaneRef.current) {
          const dimension = isHorizontal ? 'width' : 'height';
          if (collapsedPane === 'first') {
            // Batch collapse first pane styles
            firstPaneRef.current.style.cssText += `${dimension}: ${collapseSize}; flex: 0 0 auto; overflow: hidden; max-height: ${isHorizontal ? '100%' : collapseSize};`;
            secondPaneRef.current.style.cssText += `flex: 1 1 auto; ${dimension}: auto; overflow: hidden;`;
          } else {
            // Batch collapse second pane styles
            secondPaneRef.current.style.cssText += `${dimension}: ${collapseSize}; flex: 0 0 auto; overflow: hidden; max-height: ${isHorizontal ? '100%' : collapseSize};`;
            firstPaneRef.current.style.cssText += `flex: 1 1 auto; ${dimension}: auto; overflow: hidden;`;
          }
        }
      });
    }
  }, []); // Run only once on mount

  // Memoize UI elements to prevent unnecessary re-renders
  const gripDotsElement = useMemo(() => {
    if (resizable && !isCollapsed && !collapsible) {
      return (
        <View
          as="div"
          className={`split-view-divider-grip ${isHorizontal ? 'horizontal-grip' : 'vertical-grip'}`}
          aria-hidden="true"
        >
          <div className="grip-dot"></div>
          <div className="grip-dot"></div>
          <div className="grip-dot"></div>
        </View>
      );
    }
    return null;
  }, [resizable, isCollapsed, collapsible, isHorizontal]);

  // Memoize event handlers for collapse button
  const handleCollapseClick = useCallback(
    (e) => {
      e.stopPropagation();
      toggleCollapse();
    },
    [toggleCollapse]
  );

  const handleCollapseKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleCollapse();
      }
    },
    [toggleCollapse]
  );

  // Memoize collapse button to prevent unnecessary re-renders
  const collapseButtonElement = useMemo(() => {
    if (collapsible && !isCollapsed) {
      return (
        <View
          as="div"
          position="absolute"
          top={!isHorizontal && collapsedPane === 'first' ? '4px' : '50%'}
          left={
            isHorizontal
              ? collapsedPane === 'first'
                ? '4px'
                : undefined
              : '50%'
          }
          right={
            isHorizontal
              ? collapsedPane === 'second'
                ? '4px'
                : undefined
              : undefined
          }
          bottom={
            !isHorizontal
              ? collapsedPane === 'second'
                ? '4px'
                : undefined
              : undefined
          }
          transform={isHorizontal ? 'translateY(-50%)' : 'translateX(-50%)'}
          zIndex="200"
        >
          <View
            as="div"
            onClick={handleCollapseClick}
            cursor="pointer"
            display="flex"
            alignItems="center"
            justifyContent="center"
            className="collapse-expand-button"
            role="button"
            tabIndex="0"
            aria-label={`Collapse ${collapsedPane} pane`}
            onKeyDown={handleCollapseKeyDown}
          >
            {isHorizontal ? (
              collapsedPane === 'first' ? (
                <IconArrowOpenStartLine
                  width="0.7em"
                  height="0.7em"
                  color="primary-inverse"
                />
              ) : (
                <IconArrowOpenEndLine
                  width="0.7em"
                  height="0.7em"
                  color="primary-inverse"
                />
              )
            ) : collapsedPane === 'first' ? (
              <IconArrowOpenUpLine
                width="0.7em"
                height="0.7em"
                color="primary-inverse"
              />
            ) : (
              <IconArrowOpenDownLine
                width="0.7em"
                height="0.7em"
                color="primary-inverse"
              />
            )}
          </View>
        </View>
      );
    }
    return null;
  }, [
    collapsible,
    isCollapsed,
    isHorizontal,
    collapsedPane,
    handleCollapseClick,
    handleCollapseKeyDown,
  ]);

  // Ensure the divider can receive focus properly
  useEffect(() => {
    // Make sure the divider can be focused programmatically if needed
    if (dividerRef.current) {
      // Add a data attribute to help with debugging
      dividerRef.current.setAttribute(
        'data-split-layout-id',
        instanceId.current
      );
    }
  }, []);

  const secondPaneStyle = {};

  // Set fixed sizes for collapsed panes when using absolute units
  if (isCollapsed && typeof collapseSize === 'string') {
    if (collapsedPane === 'first') {
      // For first pane collapse, set exact size and flex properties
      firstPaneStyle[isHorizontal ? 'width' : 'height'] = collapseSize;
      firstPaneStyle.flexGrow = 0;
      firstPaneStyle.flexShrink = 0;
      firstPaneStyle.flexBasis = collapseSize;
    } else {
      // For second pane collapse, set exact size and flex properties
      secondPaneStyle[isHorizontal ? 'width' : 'height'] = collapseSize;
      secondPaneStyle.flexGrow = 0;
      secondPaneStyle.flexShrink = 0;
      secondPaneStyle.flexBasis = collapseSize;
    }
  }

  // Memoize first pane context value to prevent unnecessary re-renders
  const firstPaneContextValue = useMemo(
    () => ({
      width: firstPaneDimensions.width,
      height: firstPaneDimensions.height,
      isCollapsed: isCollapsed && collapsedPane === 'first',
      isResized,
      splitPercentage,
      direction,
      hidePane: () => {
        if (firstPaneRef.current) {
          firstPaneRef.current.style.display = 'none';
        }
      },
      showPane: () => {
        if (firstPaneRef.current) {
          firstPaneRef.current.style.display = '';
        }
      },
      hideDivider: () => {
        if (dividerRef.current) {
          dividerRef.current.style.display = 'none';
        }
      },
      showDivider: () => {
        if (dividerRef.current) {
          dividerRef.current.style.display = '';
        }
      },
    }),
    [
      firstPaneDimensions.width,
      firstPaneDimensions.height,
      isCollapsed,
      collapsedPane,
      isResized,
      splitPercentage,
      direction,
    ]
  );

  // Memoize second pane context value to prevent unnecessary re-renders
  const secondPaneContextValue = useMemo(
    () => ({
      width: secondPaneDimensions.width,
      height: secondPaneDimensions.height,
      isCollapsed: isCollapsed && collapsedPane === 'second',
      isResized,
      splitPercentage: 100 - splitPercentage,
      direction,
      hidePane: () => {
        if (secondPaneRef.current) {
          secondPaneRef.current.style.display = 'none';
        }
        if (dividerRef.current) {
          dividerRef.current.style.display = 'none';
        }
        if (firstPaneRef.current) {
          firstPaneRef.current.style.cssText += 'width: 100% !important; height: 100% !important;';
        }
      },
      showPane: () => {
        if (secondPaneRef.current) {
          secondPaneRef.current.style.display = '';
        }
        if (dividerRef.current) {
          dividerRef.current.style.display = '';
        }
        if (firstPaneRef.current) {
          firstPaneRef.current.style.width = '';
          firstPaneRef.current.style.height = '';
        }
      },
      hideDivider: () => {
        if (dividerRef.current) {
          dividerRef.current.style.display = 'none';
        }
      },
      showDivider: () => {
        if (dividerRef.current) {
          dividerRef.current.style.display = '';
        }
      },
    }),
    [
      secondPaneDimensions.width,
      secondPaneDimensions.height,
      isCollapsed,
      collapsedPane,
      isResized,
      splitPercentage,
      direction,
    ]
  );

  return (
    <View
      as="div"
      position="relative"
      height={height}
      width={width}
      background={background}
      overflowY="hidden"
      overflowX="hidden"
      elementRef={setContainerRef}
      className="split-view-container"
    >
      <Flex
        height="100%"
        width="100%"
        direction={isHorizontal ? 'row' : 'column'}
        alignItems="stretch"
      >
        {/* First pane with context provider */}
        <FirstPaneContext.Provider value={firstPaneContextValue}>
          <Flex.Item
            as="div"
            width={isHorizontal ? `${splitPercentage}%` : '100%'}
            height={isHorizontal ? '100%' : `${splitPercentage}%`}
            shouldGrow={false}
            shouldShrink={false}
            overflowY="hidden"
            overflowX="hidden"
            className="split-view-pane first-pane"
            elementRef={setFirstPaneRef}
          >
            {firstContent}
          </Flex.Item>
        </FirstPaneContext.Provider>

        {/* Resizer handle */}
        <Flex.Item
          as="div"

          width={isHorizontal ? '8px' : '100%'}
          height={isHorizontal ? '100%' : '8px'}
          shouldGrow={false}
          shouldShrink={false}
          padding="none"
          margin="none"
          overflowX="hidden"
          overflowY="hidden"
        >
          <View
            as="div"
            width={isHorizontal ? '8px' : '100%'}
            height={isHorizontal ? '100%' : '8px'}
            cursor={
              collapsible
                ? 'pointer'
                : resizable && !isCollapsed
                  ? isHorizontal
                    ? 'col-resize'
                    : 'row-resize'
                  : 'default'
            }
            background={isDragging || isFocused ? 'brand' : 'primary'}
            onMouseDown={isCollapsed ? undefined : handleMouseDown}
            onClick={isCollapsed ? (e) => e.stopPropagation() : undefined}
            onDoubleClick={!isCollapsed ? handleDoubleClick : undefined}
            onKeyDown={isCollapsed ? undefined : handleKeyDown}
            onFocus={useCallback(() => {
              if (!isCollapsed) {
                setIsFocused(true);
              }
            }, [isCollapsed])}
            onBlur={useCallback(() => setIsFocused(false), [])}
            role="separator"
            aria-orientation={isHorizontal ? 'vertical' : 'horizontal'}
            aria-valuenow={splitPercentage}
            aria-disabled={isCollapsed}
            tabIndex={resizable && !isCollapsed ? tabIndex : -1}
            margin="none"
            padding="none"
            position="relative"
            focusColor="info"
            focusPosition="inset"
            borderRadius="medium"
            className={`split-view-divider ${isHorizontal ? 'horizontal-divider' : 'vertical-divider'} split-view-divider-${direction} ${isDragging ? 'is-dragging' : ''} ${!resizable || isCollapsed ? 'not-resizable' : ''} ${isCollapsed ? 'is-collapsed' : ''}`}
            elementRef={setDividerRef}
          >
            {/* Show grip dots when resizable and not collapsed */}
            {resizable && !isCollapsed && !collapsible && (
              <View
                as="div"
                className={`split-view-divider-grip ${isHorizontal ? 'horizontal-grip' : 'vertical-grip'}`}
                aria-hidden="true"
              >
                <div className="grip-dot"></div>
                <div className="grip-dot"></div>
                <div className="grip-dot"></div>
              </View>
            )}

            {/* Show collapse icon in the divider when collapsible and not collapsed */}
            {collapsible && !isCollapsed && (
              <View
                as="div"
                position="absolute"
                top={!isHorizontal && collapsedPane === 'first' ? '4px' : '50%'}
                left={
                  isHorizontal
                    ? collapsedPane === 'first'
                      ? '4px'
                      : undefined
                    : '50%'
                }
                right={
                  isHorizontal
                    ? collapsedPane === 'second'
                      ? '4px'
                      : undefined
                    : undefined
                }
                bottom={
                  !isHorizontal
                    ? collapsedPane === 'second'
                      ? '4px'
                      : undefined
                    : undefined
                }
                transform={
                  isHorizontal ? 'translateY(-50%)' : 'translateX(-50%)'
                }
                zIndex="200"
              >
                <View
                  as="div"
                  onClick={handleCollapseClick}
                  cursor="pointer"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  className="collapse-expand-button"
                  role="button"
                  tabIndex="0"
                  aria-label={`Collapse ${collapsedPane} pane`}
                  onKeyDown={handleCollapseKeyDown}
                >
                  {isHorizontal ? (
                    collapsedPane === 'first' ? (
                      <IconArrowOpenStartLine
                        width="0.7em"
                        height="0.7em"
                        color="primary-inverse"
                      />
                    ) : (
                      <IconArrowOpenEndLine
                        width="0.7em"
                        height="0.7em"
                        color="primary-inverse"
                      />
                    )
                  ) : collapsedPane === 'first' ? (
                    <IconArrowOpenUpLine
                      width="0.7em"
                      height="0.7em"
                      color="primary-inverse"
                    />
                  ) : (
                    <IconArrowOpenDownLine
                      width="0.7em"
                      height="0.7em"
                      color="primary-inverse"
                    />
                  )}
                </View>
              </View>
            )}
            {isCollapsed && (
              <View
                as="div"
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                zIndex="200"
              >
                {collapseTooltipContent ? (
                  <Popover
                    renderTrigger={(_props) => (
                      <View
                        {..._props}
                        as="div"
                        onClick={handleCollapseClick}
                        cursor="pointer"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        className="collapse-expand-button"
                        role="button"
                        tabIndex="0"
                        aria-label={`Expand ${collapsedPane} pane`}
                        onKeyDown={handleCollapseKeyDown}
                      >
                        {isHorizontal ? (
                          collapsedPane === 'first' ? (
                            <IconArrowOpenEndLine
                              width="0.7em"
                              height="0.7em"
                              color="primary-inverse"
                            />
                          ) : (
                            <IconArrowOpenStartLine
                              width="0.7em"
                              height="0.7em"
                              color="primary-inverse"
                            />
                          )
                        ) : collapsedPane === 'first' ? (
                          <IconArrowOpenDownLine
                            width="0.7em"
                            height="0.7em"
                            color="primary-inverse"
                          />
                        ) : (
                          <IconArrowOpenUpLine
                            width="0.7em"
                            height="0.7em"
                            color="primary-inverse"
                          />
                        )}
                      </View>
                    )}
                    placement={collapseTooltipPlacement}
                    showDelay={100}
                    hideDelay={400}
                    shouldContainFocus={false}
                    shouldReturnFocus={false}
                    shouldCloseOnDocumentClick={false}
                    interaction="hover"
                    renderContent={collapseTooltipContent}
                  />
                ) : (
                  <View
                    as="div"
                    onClick={handleCollapseClick}
                    cursor="pointer"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    className="collapse-expand-button"
                    role="button"
                    tabIndex="0"
                    aria-label={`Expand ${collapsedPane} pane`}
                    onKeyDown={handleCollapseKeyDown}
                  >
                    {isHorizontal ? (
                      collapsedPane === 'first' ? (
                        <IconArrowOpenEndLine
                          width="0.7em"
                          height="0.7em"
                          color="primary-inverse"
                        />
                      ) : (
                        <IconArrowOpenStartLine
                          width="0.7em"
                          height="0.7em"
                          color="primary-inverse"
                        />
                      )
                    ) : collapsedPane === 'first' ? (
                      <IconArrowOpenDownLine
                        width="0.7em"
                        height="0.7em"
                        color="primary-inverse"
                      />
                    ) : (
                      <IconArrowOpenUpLine
                        width="0.7em"
                        height="0.7em"
                        color="primary-inverse"
                      />
                    )}
                  </View>
                )}
              </View>
            )}
          </View>
        </Flex.Item>

        {/* Second pane with context provider */}
        <SecondPaneContext.Provider value={secondPaneContextValue}>
          <Flex.Item
            as="div"
            width={isHorizontal ? `${100 - splitPercentage - 1}%` : '100%'}
            height={isHorizontal ? '100%' : `${100 - splitPercentage - 1}%`}
            shouldGrow={false}
            shouldShrink={false}
            overflowY="hidden"
            overflowX="hidden"
            className="split-view-pane second-pane"
            elementRef={setSecondPaneRef}
          >
            {secondContent}
          </Flex.Item>
        </SecondPaneContext.Provider>
      </Flex>
    </View>
  );
});

// Define propTypes on the wrapped component
SplitView.propTypes = {
  firstContent: PropTypes.node.isRequired,
  secondContent: PropTypes.node.isRequired,
  direction: PropTypes.oneOf(['horizontal', 'vertical']),
  initialSplit: PropTypes.number,
  defaultSplit: PropTypes.number,
  minFirstSize: PropTypes.number,
  minSecondSize: PropTypes.number,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  background: PropTypes.string,
  onResizeComplete: PropTypes.func,
  splitPercentage: PropTypes.number,
  onChange: PropTypes.func,
  tabIndex: PropTypes.number,
  resizable: PropTypes.bool,
  collapsible: PropTypes.bool,
  collapseSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  collapsedPane: PropTypes.oneOf(['first', 'second']),
  collapsed: PropTypes.bool,
  onCollapseChange: PropTypes.func,
};

// Export the forwardRef component
export default SplitView;
